import { useState } from 'react';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';

import { useTheme } from '@/theme';
import { useAppDispatch, useAppSelector } from '@/state/store';
import {
    openProfile,
    closeProfile,
    setIsInformationOpen,
    toggleDebugMenu,
    setIsLocalePickerPanelVisible,
    setIsMyRewardsOpen,
    setIsManuallyMuted,
} from '@/state/features/app';
import { pathToURL } from '@/utils/urls';
import IconButton from '@/components/IconButton';
import Icon from '@/components/Icon';

import * as styles from './styles.module.scss';

export default function FloatingHeaderBar({ classname }: { classname?: string }) {
    const theme = useTheme();
    const dispatch = useAppDispatch();

    const { isProfileOpen, isInformationOpen, isLocalePickerPanelVisible, isMyRewardsOpen, isManuallyMuted } =
        useAppSelector((state) => state.app);
    const [isExpanded, setIsExpanded] = useState(false);
    const { locationId } = useParams();

    // Some buttons are only available inside locations
    const isInLocation = locationId;
    const hasInformationDrawer = theme.metadata?.customJSON?.informationDrawer?.enabled;
    const hasProfileDrawer = theme.metadata?.customJSON?.profileDrawerV2?.enabled;
    const hasDebugMenu = theme.metadata?.customJSON?.menu?.hasDebugMenuButton;
    const hasMuteButton = true;
    const logoImagePath = theme.metadata?.customJSON?.menu?.logoImagePath;
    const hasLocaleButton = process.env.PUBLIC_SUPPORT_TRANSLATIONS === 'true';
    const hasRewardsDrawer = theme.metadata?.customJSON?.rewardsDrawer?.enabled;

    return (
        <div
            className={classNames(styles.FloatingHeaderBar, classname, {
                [styles.isExpanded]: isExpanded,
            })}
        >
            <button
                className={styles.expandButton}
                onClick={() => setIsExpanded(!isExpanded)}
                aria-label={isExpanded ? 'Collapse menu' : 'Expand menu'}
            >
                {logoImagePath ? (
                    <>
                        <img className={styles.logoIcon} src={pathToURL(logoImagePath)} alt="Logo Icon" />
                        <div className={styles.buttonStatus}>
                            <Icon type={isExpanded ? 'close' : 'menu'} size={12} />
                        </div>
                    </>
                ) : (
                    <Icon className={styles.noLogoIcon} type={isExpanded ? 'close' : 'menu'} size={30} />
                )}
            </button>
            <div className={styles.buttonsContainer} inert={!isExpanded ? '' : undefined}>
                {hasProfileDrawer && isInLocation && (
                    <IconButton
                        buttonStyle="round"
                        iconType="profile"
                        // title={isProfileOpen ? 'Close Profile' : 'Open Profile'}
                        ariaLabel={isProfileOpen ? 'Close Profile' : 'Open Profile'}
                        onClick={() => {
                            isProfileOpen ? dispatch(closeProfile()) : dispatch(openProfile());
                        }}
                        className={styles.button}
                    />
                )}
                {hasInformationDrawer && (
                    <IconButton
                        buttonStyle="round"
                        iconType="info"
                        // title={isInformationOpen ? 'Close Information' : 'Open Information'}
                        ariaLabel={isInformationOpen ? 'Close Information' : 'Open Information'}
                        onClick={() => {
                            dispatch(setIsInformationOpen(!isInformationOpen));
                        }}
                        className={styles.button}
                    />
                )}
                {hasRewardsDrawer && (
                    <IconButton
                        buttonStyle="round"
                        iconType="grade"
                        // title={isMyRewardsOpen ? 'Close My Rewards' : 'Open My Rewards'}
                        ariaLabel={isMyRewardsOpen ? 'Close My Rewards' : 'Open My Rewards'}
                        onClick={() => dispatch(setIsMyRewardsOpen(!isMyRewardsOpen))}
                        className={styles.button}
                    />
                )}
                {hasLocaleButton && (
                    <IconButton
                        buttonStyle="round"
                        iconType="translation"
                        // title={isLocalePickerPanelVisible ? 'Close Language Picker' : 'Change Language'}
                        ariaLabel={isLocalePickerPanelVisible ? 'Close Language Picker' : 'Change Language'}
                        onClick={() => {
                            dispatch(setIsLocalePickerPanelVisible(!isLocalePickerPanelVisible));
                        }}
                        className={styles.button}
                    />
                )}
                {hasMuteButton && (
                    <IconButton
                        buttonStyle="round"
                        iconType={isManuallyMuted ? 'muted' : 'unmuted'}
                        // title={isManuallyMuted ? 'Unmute' : 'Mute'}
                        ariaLabel={isManuallyMuted ? 'Unmute' : 'Mute'}
                        onClick={() => {
                            dispatch(setIsManuallyMuted(!isManuallyMuted));
                        }}
                        className={styles.button}
                    />
                )}
                {hasDebugMenu && (
                    <IconButton
                        ariaLabel="debug"
                        buttonStyle="round"
                        iconType="help"
                        onClick={() => {
                            dispatch(toggleDebugMenu());
                        }}
                        className={styles.button}
                    />
                )}
            </div>
        </div>
    );
}
